import { ReactNode, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client/react';
import { Box, Button, Link, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Tab, Tabs, Typography } from '@mui/material';
import {
  AccountTree,
  Apps,
  AutoAwesome,
  Block,
  Business,
  CheckCircle,
  Delete,
  Edit,
  Event,
  Group,
  History,
  Speed,
  LibraryBooks,
  MenuBook,
  MoreVert,
  Sell,
  Tag,
  TrendingDown,
  Campaign,
  TrendingUp,
} from '@mui/icons-material';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatDateOnly, numberFormatted } from '../../Formatters';
import { SubscriptionModel } from '../../interfaces';
import { subscriptionProductNames } from '../../interfaces.enums';
import { BadgePill, ContactChip, Stat } from '../../users/details/Hero';
import { Card } from '../../users/details/Card';
import { CountChip } from '../../users/details/CountChip';
import { InfoRow } from '../../users/details/InfoRow';
import { CANCEL_PRICE_UPDATE, GET_SUBSCRIPTIONS, GET_SUBSCRIPTION_USAGE, UPDATE_PRICE } from '../subscriptions.graphql';
import { SubscriptionDialog, SubscriptionUsage } from '../SubscriptionDialog';
import { SalesPointsList } from '../salesText';
import { DeleteSubscriptionDialog } from '../DeleteSubscriptionDialog';
import { PriceChangeDialog } from '../PriceChangeDialog';
import { SubscriptionContentDialog } from '../SubscriptionContentDialog';
import { SubscriptionAppsDialog } from '../SubscriptionAppsDialog';
import { currentGuidesGiven, describeContent, useSubscriptionGuides } from '../content';
import { ProductHero, detailsGridSx, detailsPageSx } from './ProductHero';
import { PriceCard } from './PriceCard';
import { ContentPanel } from './ContentPanel';
import { AppsPanel } from './AppsPanel';
import { UsageLimitsPanel, subscriptionRules } from './UsageLimitsPanel';
import { GET_USAGE_LIMITS } from '../../usagelimits/usagelimits.graphql';
import { UsageLimitRule } from '../../usagelimits/interfaces';
import { ChangesPanel } from './ChangesPanel';

type TabId = 'innhold' | 'apper' | 'bruksgrenser' | 'endringer';
type Dialog = 'edit' | 'delete' | 'price' | 'content' | 'apps';

export default function SubscriptionDetails() {
  const params = useParams<{ id: string }>();
  const bksId = Number(params.id);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabId = tabParam === 'apper' || tabParam === 'bruksgrenser' || tabParam === 'endringer' ? tabParam : 'innhold';
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);

  const { data, loading, error } = useQuery<{ subscriptions: SubscriptionModel[] }>(GET_SUBSCRIPTIONS);
  const { data: usageData } = useQuery<{ usage: SubscriptionUsage[] }>(GET_SUBSCRIPTION_USAGE, { fetchPolicy: 'cache-and-network' });
  const { guides } = useSubscriptionGuides();
  const { data: limitsData } = useQuery<{ usageLimits: UsageLimitRule[] }>(GET_USAGE_LIMITS);
  const [cancelPriceChange, { loading: cancelling }] = useMutation(CANCEL_PRICE_UPDATE, { refetchQueries: ['GetProductChanges'] });
  const [changePrice] = useMutation(UPDATE_PRICE, { refetchQueries: ['GetProductChanges'] });

  if (error) return <Box sx={detailsPageSx}>Feil! {error.message}</Box>;
  if (loading || !data) return <LoadingSpinner />;

  const subscriptions = data.subscriptions;
  const subscription = subscriptions.find((x) => x.bksId === bksId);
  if (!subscription)
    return (
      <Box sx={detailsPageSx}>
        <Typography variant="h5">Fant ikke abonnementet</Typography>
        <Link component={RouterLink} to="/product">
          Til produktene
        </Link>
      </Box>
    );

  const usage = usageData?.usage.find((x) => x.subscriptionId === bksId);
  const parent = subscriptions.find((x) => x.bksId === subscription.parentId);
  const upgradesTo = subscriptions.find((x) => x.bksId === subscription.upgradesTo);
  const downgradesTo = subscriptions.filter((x) => x.upgradesTo === subscription.bksId && x.isSellable);
  const price = subscription.price;
  const pending = subscription.pendingPriceChange;
  const appCount = subscription.appIds?.length ?? 0;
  const guideCount = guides && subscription.content ? currentGuidesGiven(subscription.content, subscription.product, guides).length : undefined;
  const limitCount = limitsData ? subscriptionRules(limitsData.usageLimits, bksId).length : undefined;
  const close = () => setDialog(null);

  const handleTabChange = (_: unknown, value: TabId) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'innhold') next.delete('tab');
    else next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <Box sx={detailsPageSx}>
      <ProductHero
        pills={
          <>
            {!subscription.isSellable ? (
              <BadgePill tone="error" icon={<Block />}>
                Ikke til salgs
              </BadgePill>
            ) : pending ? (
              <BadgePill tone="warning" icon={<Event />}>
                Ny pris {formatDateOnly(pending.timeOfChange)}
              </BadgePill>
            ) : (
              <BadgePill tone="success" icon={<CheckCircle />}>
                Til salgs
              </BadgePill>
            )}
            {subscription.product && (
              <BadgePill tone="primary" icon={<LibraryBooks />}>
                {subscriptionProductNames[subscription.product]}
              </BadgePill>
            )}
            {subscription.isRecommended && (
              <BadgePill tone="primary" icon={<AutoAwesome />}>
                Anbefalt i portalen
              </BadgePill>
            )}
          </>
        }
        title={subscription.name}
        chips={
          <>
            {subscription.number && <ContactChip icon={<Tag fontSize="inherit" />}>Maconomy-nr. {subscription.number}</ContactChip>}
            {parent && <ContactChip icon={<AccountTree fontSize="inherit" />}>Del av {parent.name}</ContactChip>}
          </>
        }
        stats={
          <>
            <Stat icon={<Business fontSize="inherit" />} label="Lisenser" value={numberFormatted(usage?.licenses) ?? '0'} sub="har abonnementet" />
            <Stat icon={<Group fontSize="inherit" />} label="Brukere" value={numberFormatted(usage?.users) ?? '0'} sub="med tilgang" />
            <Stat
              icon={<MenuBook fontSize="inherit" />}
              label="Anvisninger"
              value={guideCount == null ? '–' : (numberFormatted(guideCount) ?? '0')}
              sub={describeContent(subscription.content, subscription.product)}
            />
            <Stat icon={<Apps fontSize="inherit" />} label="Apper" value={String(appCount)} sub={appCount === 1 ? 'app' : 'apper'} />
          </>
        }
        actions={
          <>
            <Box sx={{ flex: 1 }} />
            <Button variant="outlined" color="inherit" startIcon={<MoreVert />} onClick={(e) => setMoreAnchor(e.currentTarget)}>
              Flere valg
            </Button>
            <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
              <MenuItem
                onClick={() => {
                  setDialog('delete');
                  setMoreAnchor(null);
                }}
              >
                <ListItemIcon>
                  <Delete fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText primary="Slett abonnement" slotProps={{ primary: { sx: { color: 'error.main' } } }} />
              </MenuItem>
            </Menu>
          </>
        }
      />

      <Box sx={detailsGridSx}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2.5 } }}>
          <PriceCard
            prices={[
              { label: '1 bruker', current: price?.singleUser, next: pending?.newPrice.singleUser },
              { label: 'Ekstra bruker', current: price?.extraUser, next: pending?.newPrice.extraUser },
            ]}
            pendingFrom={pending?.timeOfChange}
            cancelling={cancelling}
            canChange={!!price}
            onChange={() => setDialog('price')}
            onCancel={() => cancelPriceChange({ variables: { input: { subscriptionId: bksId } } })}
          />
          <Card
            title="Detaljer"
            action={
              <Button size="small" startIcon={<Edit />} onClick={() => setDialog('edit')}>
                Rediger
              </Button>
            }
          >
            <InfoRow
              hover={false}
              icon={<LibraryBooks />}
              label="Produkt"
              value={subscription.product ? subscriptionProductNames[subscription.product] : null}
            />
            <InfoRow hover={false} icon={<Tag />} label="Maconomy-nr." value={subscription.number} />
            <InfoRow hover={false} icon={<Sell />} label="Til salgs" value={subscription.isSellable ? 'Ja' : 'Nei'} />
            <InfoRow
              hover={false}
              icon={<Campaign />}
              label="Salgstekst"
              value={subscription.salesText ?? null}
              valueRender={(text) => <SalesPointsList text={text} />}
            />
            <InfoRow
              hover={false}
              icon={<TrendingUp />}
              label="Kan oppgraderes til"
              value={upgradesTo?.name ?? null}
              valueRender={(name) => (
                <Link component={RouterLink} to={`/product/${upgradesTo!.bksId}`} underline="hover">
                  {name}
                </Link>
              )}
            />
            {downgradesTo.length > 0 && (
              <InfoRow hover={false} icon={<TrendingDown />} label="Kan nedgraderes til" value={downgradesTo.map((x) => x.name).join(', ')} />
            )}
            {parent && (
              <InfoRow
                hover={false}
                icon={<AccountTree />}
                label="Del av"
                value={parent.name}
                valueRender={(name) => (
                  <Link component={RouterLink} to={`/product/${parent.bksId}`} underline="hover">
                    {name}
                  </Link>
                )}
              />
            )}
          </Card>
        </Box>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: 'divider', pl: { xs: 0, sm: 1.5 } }}
          >
            <Tab
              value="innhold"
              icon={<MenuBook fontSize="small" />}
              iconPosition="start"
              label={
                <TabLabel active={activeTab === 'innhold'} count={guideCount}>
                  Innhold
                </TabLabel>
              }
            />
            <Tab
              value="apper"
              icon={<Apps fontSize="small" />}
              iconPosition="start"
              label={
                <TabLabel active={activeTab === 'apper'} count={appCount}>
                  Apper
                </TabLabel>
              }
            />
            <Tab
              value="bruksgrenser"
              icon={<Speed fontSize="small" />}
              iconPosition="start"
              label={
                <TabLabel active={activeTab === 'bruksgrenser'} count={limitCount}>
                  Bruksgrenser
                </TabLabel>
              }
            />
            <Tab
              value="endringer"
              icon={<History fontSize="small" />}
              iconPosition="start"
              label={<TabLabel active={activeTab === 'endringer'}>Endringer</TabLabel>}
            />
          </Tabs>

          {activeTab === 'innhold' && <ContentPanel subscription={subscription} onChange={() => setDialog('content')} />}
          {activeTab === 'apper' && <AppsPanel subscription={subscription} onChange={() => setDialog('apps')} />}
          {activeTab === 'bruksgrenser' && <UsageLimitsPanel subscription={subscription} subscriptions={subscriptions} />}
          {activeTab === 'endringer' && (
            <ChangesPanel
              kind="Subscription"
              subscriptionId={bksId}
              revision={[
                subscription.name,
                subscription.number,
                subscription.isSellable,
                price?.singleUser,
                price?.extraUser,
                pending?.id,
                subscription.content,
                subscription.appIds,
              ]}
            />
          )}
        </Paper>
      </Box>

      {dialog === 'price' && price && (
        <PriceChangeDialog
          product={subscription.name}
          fields={[
            { label: '1 bruker', current: price.singleUser },
            { label: 'Ekstra bruker', current: price.extraUser },
          ]}
          onSave={([singleUser, extraUser], timeOfChange) =>
            changePrice({ variables: { input: { subscriptionId: bksId, singleUser, extraUser, timeOfChange: timeOfChange.toISOString() } } })
          }
          onClose={close}
        />
      )}
      {dialog === 'content' && subscription.product && (
        <SubscriptionContentDialog subscription={{ ...subscription, product: subscription.product }} usage={usage} onClose={close} />
      )}
      {dialog === 'apps' && <SubscriptionAppsDialog subscription={subscription} usage={usage} onClose={close} />}
      {dialog === 'edit' && <SubscriptionDialog subscription={subscription} subscriptions={subscriptions} usage={usage} onClose={close} />}
      {dialog === 'delete' && <DeleteSubscriptionDialog subscription={subscription} onClose={close} onDeleted={() => navigate('/product')} />}
    </Box>
  );
}

function TabLabel({ active, count, children }: { active: boolean; count?: number; children: ReactNode }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      {children}
      {count !== undefined && <CountChip count={count} active={active} />}
    </Box>
  );
}
